import React, { useState } from 'react';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import Modal from '@amzn/awsui-components-console/modal';
import FormField from '@amzn/awsui-components-console/form-field';
import Textarea from '@amzn/awsui-components-console/textarea';
import Input from '@amzn/awsui-components-console/input';
import Alert from '@amzn/awsui-components-console/alert';

interface SourceTextPanelProps {
  sourceLocale: string;
  sourceText: string;
  contextNote?: string | null;
  /** CX ID when in variant view */
  variantCxId?: string;
  /** Base string source text for comparison (variant view only) */
  baseSourceText?: string | null;
  /** Whether the base source text is still loading */
  baseSourceTextLoading?: boolean;
  /** String ID for the edit modal */
  stringId?: string;
}

/**
 * DEMO-ONLY EDIT MODAL:
 * This modal replicates the static HTML mockup's "Edit string" / "Edit variant" modal.
 * The mockup itself has no real persistence — it simply calls hideModal() + toast('String updated!').
 * No UpdateString/UpdateStringVariant GraphQL mutation exists in the backend schema today.
 * A real implementation would require adding the mutation to the Smithy model, GraphQL schema,
 * AppSync resolver, and API Gateway handler before this could persist changes.
 */
const SourceTextPanel: React.FC<SourceTextPanelProps> = ({
  sourceLocale,
  sourceText,
  contextNote,
  variantCxId,
  baseSourceText,
  baseSourceTextLoading,
  stringId,
}) => {
  const isVariant = !!variantCxId;

  // Edit modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editSourceText, setEditSourceText] = useState('');
  const [editContextNote, setEditContextNote] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const handleEditClick = () => {
    setEditSourceText(sourceText);
    setEditContextNote(contextNote || '');
    setEditModalVisible(true);
  };

  const handleSave = () => {
    // DEMO-ONLY: No backend mutation exists. This mirrors the static mockup's
    // behavior of closing the modal and showing a success toast with no actual persistence.
    setEditModalVisible(false);
    setShowSuccessAlert(true);
    // Auto-dismiss after 4 seconds
    setTimeout(() => setShowSuccessAlert(false), 4000);
  };

  const handleCancel = () => {
    setEditModalVisible(false);
  };

  return (
    <>
      {/* Demo-only success notification after "saving" */}
      {showSuccessAlert && (
        <Alert
          type="success"
          dismissible
          onDismiss={() => setShowSuccessAlert(false)}
        >
          {isVariant ? 'Variant updated!' : 'String updated!'}
        </Alert>
      )}

      <Container
        header={
          <Header
            actions={
              <Button
                variant="normal"
                onClick={handleEditClick}
                data-testid={
                  isVariant ? 'edit-variant-button' : 'edit-string-button'
                }
              >
                Edit
              </Button>
            }
          >
            Source Text
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/* Base source text comparison (variant view only) */}
          {isVariant && (
            <div>
              <Box variant="awsui-key-label" color="text-status-inactive">
                <em>{`Base source text (${sourceLocale})`}</em>
              </Box>
              {baseSourceTextLoading ? (
                <Box variant="p">
                  <Spinner size="normal" /> Loading base text…
                </Box>
              ) : (
                <Box
                  variant="p"
                  color="text-status-inactive"
                  data-testid="base-source-text"
                >
                  {baseSourceText ?? '—'}
                </Box>
              )}
            </div>
          )}

          {/* Divider between base and variant text */}
          {isVariant && <hr aria-hidden="true" />}

          <div>
            <Box
              variant="awsui-key-label"
              data-testid={
                isVariant ? 'variant-source-label' : 'source-locale-label'
              }
            >
              {isVariant
                ? `Variant source text (${sourceLocale}) — ${variantCxId}`
                : `Source text (${sourceLocale})`}
            </Box>
            <Box
              variant="p"
              data-testid={isVariant ? 'variant-source-text' : 'source-text'}
            >
              {sourceText}
            </Box>
          </div>
          {contextNote && (
            <div>
              <Box variant="awsui-key-label">Context note</Box>
              <Box
                variant="p"
                data-testid={
                  isVariant ? 'variant-context-note' : 'context-note'
                }
              >
                {contextNote}
              </Box>
            </div>
          )}
        </SpaceBetween>
      </Container>

      {/* DEMO-ONLY: Edit modal matching the static HTML mockup's modal.
          No real backend mutation exists — see component JSDoc above. */}
      {editModalVisible && (
        <Modal
          visible={editModalVisible}
          onDismiss={handleCancel}
          header={isVariant ? 'Edit variant' : 'Edit string'}
          footer={
            <Box float="right">
              <SpaceBetween size="xs" direction="horizontal">
                <Button
                  variant="link"
                  onClick={handleCancel}
                  data-testid={
                    isVariant
                      ? 'edit-variant-cancel-button'
                      : 'edit-cancel-button'
                  }
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  data-testid={
                    isVariant ? 'edit-variant-save-button' : 'edit-save-button'
                  }
                >
                  Save changes
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <div
            data-testid={isVariant ? 'edit-variant-modal' : 'edit-string-modal'}
          >
            <SpaceBetween size="m">
              <FormField label="String ID">
                <Input
                  value={stringId || ''}
                  disabled
                  data-testid={
                    isVariant
                      ? 'edit-variant-string-id'
                      : 'edit-string-id-input'
                  }
                />
              </FormField>
              {isVariant && (
                <FormField label="CX ID">
                  <Input
                    value={variantCxId || ''}
                    disabled
                    data-testid="edit-variant-cxid"
                  />
                </FormField>
              )}
              {!isVariant && (
                <FormField label="Source locale">
                  <Input
                    value={sourceLocale}
                    disabled
                    data-testid="edit-source-locale-select"
                  />
                </FormField>
              )}
              <FormField label="Source text">
                <Textarea
                  value={editSourceText}
                  onChange={({ detail }) => setEditSourceText(detail.value)}
                  rows={4}
                  data-testid={
                    isVariant ? 'edit-variant-source-text' : 'edit-source-text'
                  }
                />
              </FormField>
              <FormField label="Context note">
                <Textarea
                  value={editContextNote}
                  onChange={({ detail }) => setEditContextNote(detail.value)}
                  rows={3}
                  data-testid={
                    isVariant
                      ? 'edit-variant-context-note'
                      : 'edit-context-note'
                  }
                />
              </FormField>
              {/* Note: The static mockup includes Inline Markup (Plain Text/HTML/Markdown) and
                  Message Format (None/Hash-Get/Amazon Message Format) radio groups. These are
                  omitted here as they require additional data model fields not used elsewhere
                  on this page and would add visual complexity without functional value. */}
            </SpaceBetween>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SourceTextPanel;
